import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Typography,
  alpha,
} from '@mui/material';

// Helper functions for UTC/Local time conversion with day adjustments
const convertLocalTimeToUTC = (
  hour: string,
  minute: string,
  dayOfWeek?: string,
  dayOfMonth?: string,
  month?: string
): {
  utcHour: string;
  utcMinute: string;
  utcDayOfWeek?: string;
  utcDayOfMonth?: string;
  utcMonth?: string;
} => {
  const today = new Date();
  const localDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    parseInt(hour),
    parseInt(minute)
  );

  const utcDate = new Date(localDate.getTime());
  const utcHour = utcDate.getUTCHours().toString().padStart(2, '0');
  const utcMinute = utcDate.getUTCMinutes().toString().padStart(2, '0');

  let result: any = { utcHour, utcMinute };

  // Calculate day difference
  const dayDifference = utcDate.getUTCDate() - localDate.getDate();

  // Handle day of week adjustment
  if (dayOfWeek) {
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const currentDayIndex = daysOfWeek.indexOf(dayOfWeek);
    let newDayIndex = currentDayIndex + dayDifference;

    if (newDayIndex < 0) newDayIndex = 6;
    if (newDayIndex > 6) newDayIndex = 0;

    result.utcDayOfWeek = daysOfWeek[newDayIndex];
  }

  // Handle day of month adjustment
  if (dayOfMonth && dayOfMonth !== 'L') {
    const localDay = parseInt(dayOfMonth);
    const utcDay = localDay + dayDifference;

    if (utcDay <= 0) {
      // Previous month - handle month boundary
      result.utcDayOfMonth = 'L'; // For simplicity, use last day
    } else if (utcDay > 31) {
      // Next month - handle month boundary
      result.utcDayOfMonth = '01';
    } else {
      result.utcDayOfMonth = utcDay.toString().padStart(2, '0');
    }
  } else if (dayOfMonth === 'L') {
    result.utcDayOfMonth = 'L';
  }

  // Handle month adjustment for yearly schedules
  if (month && dayOfMonth && dayOfMonth !== 'L') {
    const localMonth = parseInt(month);
    const localDay = parseInt(dayOfMonth);
    const utcDay = localDay + dayDifference;

    if (utcDay <= 0) {
      // Previous month
      let newMonth = localMonth - 1;
      if (newMonth <= 0) newMonth = 12;
      result.utcMonth = newMonth.toString().padStart(2, '0');
      result.utcDayOfMonth = 'L'; // Last day of previous month
    } else if (utcDay > 31) {
      // Next month (simplified)
      let newMonth = localMonth + 1;
      if (newMonth > 12) newMonth = 1;
      result.utcMonth = newMonth.toString().padStart(2, '0');
      result.utcDayOfMonth = '01';
    } else {
      result.utcMonth = month;
      result.utcDayOfMonth = utcDay.toString().padStart(2, '0');
    }
  } else if (month) {
    result.utcMonth = month;
  }

  return result;
};

const convertUTCTimeToLocal = (
  hour: string,
  minute: string,
  dayOfWeek?: string,
  dayOfMonth?: string,
  month?: string
): {
  localHour: string;
  localMinute: string;
  localDayOfWeek?: string;
  localDayOfMonth?: string;
  localMonth?: string;
} => {
  const today = new Date();
  const utcDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      parseInt(hour),
      parseInt(minute)
    )
  );

  const localHour = utcDate.getHours().toString().padStart(2, '0');
  const localMinute = utcDate.getMinutes().toString().padStart(2, '0');

  let result: any = { localHour, localMinute };

  // Calculate day difference
  const dayDifference = utcDate.getDate() - utcDate.getUTCDate();

  // Handle day of week adjustment
  if (dayOfWeek) {
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const currentDayIndex = daysOfWeek.indexOf(dayOfWeek);
    let newDayIndex = currentDayIndex + dayDifference;

    if (newDayIndex < 0) newDayIndex = 6;
    if (newDayIndex > 6) newDayIndex = 0;

    result.localDayOfWeek = daysOfWeek[newDayIndex];
  }

  // Handle day of month adjustment
  if (dayOfMonth && dayOfMonth !== 'L') {
    const utcDay = parseInt(dayOfMonth);
    const localDay = utcDay + dayDifference;

    if (localDay <= 0) {
      result.localDayOfMonth = 'L'; // Last day of previous month
    } else if (localDay > 31) {
      result.localDayOfMonth = '01'; // First day of next month
    } else {
      result.localDayOfMonth = localDay.toString().padStart(2, '0');
    }
  } else if (dayOfMonth === 'L') {
    result.localDayOfMonth = 'L';
  }

  // Handle month adjustment for yearly schedules
  if (month && dayOfMonth && dayOfMonth !== 'L') {
    const utcMonth = parseInt(month);
    const utcDay = parseInt(dayOfMonth);
    const localDay = utcDay + dayDifference;

    if (localDay <= 0) {
      let newMonth = utcMonth - 1;
      if (newMonth <= 0) newMonth = 12;
      result.localMonth = newMonth.toString().padStart(2, '0');
      result.localDayOfMonth = 'L';
    } else if (localDay > 31) {
      let newMonth = utcMonth + 1;
      if (newMonth > 12) newMonth = 1;
      result.localMonth = newMonth.toString().padStart(2, '0');
      result.localDayOfMonth = '01';
    } else {
      result.localMonth = month;
      result.localDayOfMonth = localDay.toString().padStart(2, '0');
    }
  } else if (month) {
    result.localMonth = month;
  }

  return result;
};

const getTimezoneOffset = (): string => {
  const offset = new Date().getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  return `${sign}${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
};

export interface InstantExpressionGeneratorProps {
  value?: string; // expresión inicial (UTC)
  onChange?: (expression: string) => void;
  text?: string; // texto del título
}

function TabPanel({ children, value, index }: any) {
  return (
    <div role='tabpanel' hidden={value !== index}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const InstantExpressionGenerator: React.FC<InstantExpressionGeneratorProps> = ({
  value,
  onChange,
  text = 'Generador de Expresiones Instantáneas',
}) => {
  // Tabs: 0=Diario, 1=Semanal, 2=Mensual, 3=Anual, 4=TodoElTiempo
  const [activeTab, setActiveTab] = useState(0);
  // Daily
  const [dailyHour, setDailyHour] = useState('00');
  const [dailyMinute, setDailyMinute] = useState('00');
  // Weekly
  const [weeklyDay, setWeeklyDay] = useState('Mon');
  const [weeklyHour, setWeeklyHour] = useState('00');
  const [weeklyMinute, setWeeklyMinute] = useState('00');
  // Monthly
  const [monthlyDay, setMonthlyDay] = useState('1');
  const [monthlyHour, setMonthlyHour] = useState('00');
  const [monthlyMinute, setMonthlyMinute] = useState('00');
  // Yearly
  const [yearlyMonth, setYearlyMonth] = useState('01');
  const [yearlyDay, setYearlyDay] = useState('01');
  const [yearlyHour, setYearlyHour] = useState('00');
  const [yearlyMinute, setYearlyMinute] = useState('00');

  // Parse initial value
  useEffect(() => {
    if (!value) return;

    // Prevenir loops infinitos - solo parsear si el value realmente cambió desde fuera
    const currentExpression = generateExpression();
    if (currentExpression === value) return;

    try {
      if (value.startsWith('DAILY@')) {
        const timeStr = value.replace('DAILY@', '');
        const [utcHour, utcMinute] = timeStr.split(':');
        if (utcHour && utcMinute) {
          const { localHour, localMinute } = convertUTCTimeToLocal(
            utcHour,
            utcMinute
          );
          setDailyHour(localHour);
          setDailyMinute(localMinute);
          setActiveTab(0);
        }
      } else if (value.startsWith('WEEKLY@')) {
        const parts = value.replace('WEEKLY@', '').split('@');
        if (parts.length >= 2) {
          const day = parts[0];
          const timeParts = parts[1].split(':');
          if (timeParts.length >= 2) {
            const [utcHour, utcMinute] = timeParts;
            const { localHour, localMinute, localDayOfWeek } =
              convertUTCTimeToLocal(utcHour, utcMinute, day);
            setWeeklyDay(localDayOfWeek || day);
            setWeeklyHour(localHour);
            setWeeklyMinute(localMinute);
            setActiveTab(1);
          }
        }
      } else if (value.startsWith('MONTHLY@')) {
        const parts = value.replace('MONTHLY@', '').split('@');
        if (parts.length >= 2) {
          const day = parts[0];
          const timeParts = parts[1].split(':');
          if (timeParts.length >= 2) {
            const [utcHour, utcMinute] = timeParts;
            const { localHour, localMinute, localDayOfMonth } =
              convertUTCTimeToLocal(utcHour, utcMinute, undefined, day);
            setMonthlyDay(
              localDayOfMonth === 'L'
                ? 'L'
                : (localDayOfMonth || day).padStart(2, '0')
            );
            setMonthlyHour(localHour);
            setMonthlyMinute(localMinute);
            setActiveTab(2);
          }
        }
      } else if (value.startsWith('YEARLY@')) {
        const parts = value.replace('YEARLY@', '').split('@');
        if (parts.length >= 2) {
          const dateParts = parts[0].split('-');
          const timeParts = parts[1].split(':');
          if (dateParts.length >= 2 && timeParts.length >= 2) {
            const [month, day] = dateParts;
            const [utcHour, utcMinute] = timeParts;
            const { localHour, localMinute, localMonth, localDayOfMonth } =
              convertUTCTimeToLocal(utcHour, utcMinute, undefined, day, month);
            setYearlyMonth((localMonth || month).padStart(2, '0'));
            setYearlyDay(
              localDayOfMonth === 'L'
                ? 'L'
                : (localDayOfMonth || day).padStart(2, '0')
            );
            setYearlyHour(localHour);
            setYearlyMinute(localMinute);
            setActiveTab(3);
          }
        }
      } else if (value === 'ALLTIME') {
        setActiveTab(4);
      }
    } catch (error) {
      console.error('Error parsing instant expression:', error);
    }
  }, [value]); // Solo depende de value

  // Generate expression
  const generateExpression = () => {
    switch (activeTab) {
      case 0: {
        const { utcHour, utcMinute } = convertLocalTimeToUTC(
          dailyHour,
          dailyMinute
        );
        return `DAILY@${utcHour}:${utcMinute}`;
      }
      case 1: {
        const { utcHour, utcMinute, utcDayOfWeek } = convertLocalTimeToUTC(
          weeklyHour,
          weeklyMinute,
          weeklyDay
        );
        return `WEEKLY@${utcDayOfWeek}@${utcHour}:${utcMinute}`;
      }
      case 2: {
        const { utcHour, utcMinute, utcDayOfMonth } = convertLocalTimeToUTC(
          monthlyHour,
          monthlyMinute,
          undefined,
          monthlyDay
        );
        return `MONTHLY@${utcDayOfMonth}@${utcHour}:${utcMinute}`;
      }
      case 3: {
        const { utcHour, utcMinute, utcMonth, utcDayOfMonth } =
          convertLocalTimeToUTC(
            yearlyHour,
            yearlyMinute,
            undefined,
            yearlyDay,
            yearlyMonth
          );
        const formattedDay =
          utcDayOfMonth === 'L' ? 'L' : utcDayOfMonth?.padStart(2, '0');
        return `YEARLY@${utcMonth}-${formattedDay}@${utcHour}:${utcMinute}`;
      }
      case 4:
        return 'ALLTIME';
      default:
        return '';
    }
  };

  // Emit onChange when values change
  // Emit onChange when values change - pero con debounce para evitar loops
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onChange) {
        const newExpression = generateExpression();
        onChange(newExpression);
      }
    }, 100); // Pequeño delay para evitar múltiples llamadas

    return () => clearTimeout(timeoutId);
  }, [
    activeTab,
    dailyHour,
    dailyMinute,
    weeklyDay,
    weeklyHour,
    weeklyMinute,
    monthlyDay,
    monthlyHour,
    monthlyMinute,
    yearlyMonth,
    yearlyDay,
    yearlyHour,
    yearlyMinute,
    onChange, // Incluir onChange en las dependencias
  ]);
  // Options
  const hourOptions = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, '0')
  );
  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, '0')
  );
  const dayOptions = [
    ...Array.from({ length: 31 }, (_, i) =>
      (i + 1).toString().padStart(2, '0')
    ),
    'L',
  ];
  const monthOptions = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, '0')
  );
  const weekDayOptions = [
    { value: 'Sun', label: 'Domingo' },
    { value: 'Mon', label: 'Lunes' },
    { value: 'Tue', label: 'Martes' },
    { value: 'Wed', label: 'Miércoles' },
    { value: 'Thu', label: 'Jueves' },
    { value: 'Fri', label: 'Viernes' },
    { value: 'Sat', label: 'Sábado' },
  ];

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2, ml: 1 }}>
        {text}
      </Typography>
      <Tabs
        variant='fullWidth'
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        centered
        sx={{ mb: 2 }}
      >
        <Tab label='Diario' />
        <Tab label='Semanal' />
        <Tab label='Mensual' />
        <Tab label='Anual' />
        <Tab label='Todo el Tiempo' />
      </Tabs>

      {/* Diario */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label='Hora'
              value={dailyHour}
              onChange={e => setDailyHour(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {hourOptions.map(h => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label='Minutos'
              value={dailyMinute}
              onChange={e => setDailyMinute(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {minuteOptions.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Semanal */}
      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label='Día de la semana'
              value={weeklyDay}
              onChange={e => setWeeklyDay(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {weekDayOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label='Hora'
              value={weeklyHour}
              onChange={e => setWeeklyHour(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {hourOptions.map(h => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label='Minutos'
              value={weeklyMinute}
              onChange={e => setWeeklyMinute(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {minuteOptions.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Mensual */}
      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label='Día'
              value={monthlyDay}
              onChange={e => setMonthlyDay(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {dayOptions.map(d => (
                <MenuItem key={d} value={d}>
                  {d === 'L' ? 'Último día' : d}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label='Hora'
              value={monthlyHour}
              onChange={e => setMonthlyHour(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {hourOptions.map(h => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label='Minutos'
              value={monthlyMinute}
              onChange={e => setMonthlyMinute(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {minuteOptions.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Anual */}
      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label='Mes'
              value={yearlyMonth}
              onChange={e => setYearlyMonth(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {monthOptions.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label='Día'
              value={yearlyDay}
              onChange={e => setYearlyDay(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {dayOptions.map(d => (
                <MenuItem key={d} value={d}>
                  {d === 'L' ? 'Último día' : d}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label='Hora'
              value={yearlyHour}
              onChange={e => setYearlyHour(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {hourOptions.map(h => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label='Minutos'
              value={yearlyMinute}
              onChange={e => setYearlyMinute(e.target.value)}
              fullWidth
              SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
            >
              {minuteOptions.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Todo el Tiempo */}
      <TabPanel value={activeTab} index={4}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            px: 2,
          }}
        >
          <Typography variant='body1' sx={{ textAlign: 'center', mb: 2 }}>
            Esta opción calcula desde el primer valor disponible en el
            histórico.
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ textAlign: 'center' }}
          >
            No requiere configuración adicional de fecha y hora.
          </Typography>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default InstantExpressionGenerator;
